import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import './Checkbox.css';

/**
 * Checkbox Component
 * Styled checkbox with label
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Checkbox label
 * @param {boolean} props.checked - Checked state
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional CSS classes
 */
const Checkbox = forwardRef(
  (
    {
      label,
      checked = false,
      onChange,
      disabled = false,
      error,
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const containerClasses = `
      checkbox-container
      ${disabled ? 'checkbox-container--disabled' : ''}
      ${error ? 'checkbox-container--error' : ''}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className={containerClasses}>
        <label htmlFor={checkboxId} className="checkbox-label-wrapper">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            name={name}
            className="checkbox-input"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={error ? `${checkboxId}-error` : undefined}
            {...props}
          />

          <motion.div
            className={`checkbox-custom ${checked ? 'checkbox-custom--checked' : ''}`}
            animate={{ scale: checked ? [1, 1.1, 1] : 1 }}
            transition={{ duration: 0.2 }}
          >
            {checked && (
              <motion.svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <path
                  d="M13.3334 4L6.00008 11.3333L2.66675 8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.svg>
            )}
          </motion.div>

          {label && <span className="checkbox-label">{label}</span>}
        </label>

        {error && (
          <motion.span
            id={`${checkboxId}-error`}
            className="checkbox-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            role="alert"
          >
            {error}
          </motion.span>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Checkbox;
